import React, { useState, useEffect } from 'react';
import { reportsAPI } from '../services/api';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement } from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement);

const Reports: React.FC = () => {
  const [clientSummary, setClientSummary] = useState<any>(null);
  const [deviceStatus, setDeviceStatus] = useState<any>(null);
  const [kpis, setKpis] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const [clientRes, deviceRes, kpiRes] = await Promise.all([
        reportsAPI.getClientSummary(),
        reportsAPI.getDeviceStatus(),
        reportsAPI.getKPIs()
      ]);
      
      setClientSummary(clientRes.data);
      setDeviceStatus(deviceRes.data);
      setKpis(kpiRes.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ padding: '20px', textAlign: 'center' }}>
        <div>Loading reports...</div>
      </div>
    );
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
    },
  };

  const pieChartData = {
    labels: ['Active Devices', 'Inactive Devices'],
    datasets: [{
      data: [deviceStatus?.active || 0, deviceStatus?.inactive || 0],
      backgroundColor: ['#1d4ed8', '#e5e7eb'],
      borderColor: ['#1e40af', '#d1d5db'],
      borderWidth: 2,
    }],
  };

  const barChartData = {
    labels: ['Total Clients', 'Active Devices', 'Monthly Revenue'],
    datasets: [{
      label: 'Metrics',
      data: [
        clientSummary?.total_clients || 0,
        deviceStatus?.active || 0,
        kpis?.monthly_revenue || 0
      ],
      backgroundColor: '#1d4ed8',
      borderColor: '#1e40af',
      borderWidth: 1,
    }],
  };

  const lineChartData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [{
      label: 'Revenue Trend',
      data: [1200, 1900, 3000, 5000, 2000, 3000],
      borderColor: '#1d4ed8',
      backgroundColor: 'rgba(29, 78, 216, 0.1)',
      tension: 0.4,
    }],
  };

  return (
    <div className="container" style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '600', color: '#333', marginBottom: '10px' }}>
          Reports & Analytics
        </h1>
        <p style={{ color: '#666', marginBottom: '0' }}>
          Overview of system performance and key metrics
        </p>
      </div>

      {/* KPI Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '20px', 
        marginBottom: '30px' 
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', margin: '0 0 8px 0', textTransform: 'uppercase' }}>
            Total Clients
          </h3>
          <p style={{ fontSize: '32px', fontWeight: '700', color: '#1d4ed8', margin: '0' }}>
            {clientSummary?.total_clients || 0}
          </p>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', margin: '0 0 8px 0', textTransform: 'uppercase' }}>
            Active Devices
          </h3>
          <p style={{ fontSize: '32px', fontWeight: '700', color: '#059669', margin: '0' }}>
            {deviceStatus?.active || 0}
          </p>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', margin: '0 0 8px 0', textTransform: 'uppercase' }}>
            Monthly Revenue
          </h3>
          <p style={{ fontSize: '32px', fontWeight: '700', color: '#dc2626', margin: '0' }}>
            ${kpis?.monthly_revenue?.toLocaleString() || 0}
          </p>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', margin: '0 0 8px 0', textTransform: 'uppercase' }}>
            Average Fee
          </h3>
          <p style={{ fontSize: '32px', fontWeight: '700', color: '#7c3aed', margin: '0' }}>
            ${clientSummary?.average_monthly_fee?.toFixed(2) || 0}
          </p>
        </div>
      </div>

      {/* Charts Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', 
        gap: '20px' 
      }}>
        {/* Device Status Pie Chart */}
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#333', marginBottom: '20px' }}>
            Device Status Distribution
          </h3>
          <div style={{ height: '300px' }}>
            <Pie data={pieChartData} options={chartOptions} />
          </div>
        </div>

        {/* Metrics Bar Chart */}
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#333', marginBottom: '20px' }}>
            Key Metrics Overview
          </h3>
          <div style={{ height: '300px' }}>
            <Bar data={barChartData} options={chartOptions} />
          </div>
        </div>

        {/* Revenue Trend Line Chart */}
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb',
          gridColumn: '1 / -1'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#333', marginBottom: '20px' }}>
            Revenue Trend (6 Months)
          </h3>
          <div style={{ height: '300px' }}>
            <Line data={lineChartData} options={chartOptions} />
          </div>
        </div>
      </div>

      {/* Summary Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb',
        marginTop: '30px',
        overflow: 'hidden'
      }}>
        <div style={{ padding: '24px', borderBottom: '1px solid #e5e7eb' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#333', margin: '0' }}>
            System Summary
          </h3>
        </div>
        <div style={{ padding: '24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
            <div>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 4px 0' }}>Total Clients</p>
              <p style={{ fontSize: '20px', fontWeight: '600', color: '#333', margin: '0' }}>
                {clientSummary?.total_clients || 0}
              </p>
            </div>
            <div>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 4px 0' }}>Active Devices</p>
              <p style={{ fontSize: '20px', fontWeight: '600', color: '#333', margin: '0' }}>
                {deviceStatus?.active || 0}
              </p>
            </div>
            <div>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 4px 0' }}>Inactive Devices</p>
              <p style={{ fontSize: '20px', fontWeight: '600', color: '#333', margin: '0' }}>
                {deviceStatus?.inactive || 0}
              </p>
            </div>
            <div>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 4px 0' }}>Total Revenue</p>
              <p style={{ fontSize: '20px', fontWeight: '600', color: '#333', margin: '0' }}>
                ${kpis?.monthly_revenue?.toLocaleString() || 0}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
